import { createApp, configureCompat } from 'vue'
import App from './App.vue'
import router from './router'
import axios from 'axios'
import VueAxios from 'vue-axios'
import Notifications from '@kyvg/vue3-notification'
import Default from './layouts/Default'
import VueCardFormat from 'vue-credit-card-validation'
import aim from './plugins/aim'
import { bus } from './eventBus'
import BootstrapVueNextCompat from './plugins/bootstrapVueNextCompat'
import BvModalCompat from './plugins/bvModalCompat'

// bootstrap 5
import { createBootstrap } from 'bootstrap-vue-next'
import 'bootstrap/dist/css/bootstrap.css'
import 'bootstrap-icons/font/bootstrap-icons.css'
import 'bootstrap-vue-next/dist/bootstrap-vue-next.css'
import 'video.js/dist/video-js.css'

import UserService from '@/services/UserService'

configureCompat({
  MODE: 2,

  // Required for bootstrap-vue-next components like BModal, BFormInput, BFormSelect
  COMPONENT_V_MODEL: false,
  ATTR_FALSE_VALUE: false,
  INSTANCE_ATTRS_CLASS_STYLE: false,
  // bootstrap-vue-next relies on Vue 3 listener fallthrough. With this on (MODE 2 default) any
  // listener a component doesn't declare in `emits` is dropped: BNavForm/BForm @submit (navbar search
  // reloaded the page instead of opening the modal), BCard @click, BFormInput/Select/Textarea @blur
  INSTANCE_LISTENERS: false
})

let apiUrl = 'https://admin.sbnbroadcasting.com/api/v1/'
let hostName = window.location.hostname
const isDevHostname = /\.dev\.aimitservices\.com$/.test(hostName) || hostName === 'localhost' || hostName === '127.0.0.1'
if ((typeof process !== 'undefined' && process.env.NODE_ENV === 'development') || isDevHostname) {
  apiUrl = 'https://thespinnetwork.dev.aimitservices.com/api/v1/'
  // Override hostname to the configured "network" identifier so the dev API
  // can match a network record by hostname header.
  if (typeof process !== 'undefined' && process.env.VUE_APP_NETWORK) {
    hostName = process.env.VUE_APP_NETWORK
  }
} else if (typeof process !== 'undefined' && process.env.NODE_ENV === 'staging') {
  apiUrl = process.env.VUE_APP_STAGING_API_URL
  hostName = process.env.VUE_APP_NETWORK_URL
}
const canSwitchNetwork =
  isDevHostname ||
  (typeof process !== 'undefined' && ['development', 'staging'].includes(process.env.NODE_ENV))
// allow switching of network based GET param in staging/development
if (canSwitchNetwork) {
  const params = new URLSearchParams(window.location.search)
  const networkUrl = params.get('NETWORK_URL')

  if (networkUrl) {
    hostName = networkUrl
    localStorage.setItem('hostName', hostName)
  } else {
    const lsHost = localStorage.getItem('hostName')
    if (lsHost) {
      hostName = lsHost
    }
  }
}

// set api url
axios.defaults.baseURL = apiUrl
// set  axios defaults to always set content type to JSON by default.
axios.defaults.headers.common['Content-Type'] = 'application/json'
axios.defaults.headers.common.Accept = 'application/json'

// Set our NETWORK header to identify which spin network this is
axios.defaults.headers.common.Network = hostName

// enable xdebug on api calls in development
if (process.env.NODE_ENV === 'development') {
  axios.defaults.params = {}
  axios.defaults.params.XDEBUG_SESSION = 'mobile'
}

// nothing defined here (when this.$route.path is other than "/")
// intercept all the axios request
axios.interceptors.response.use(
  response => response,
  err => {
    if (!err.response) {
      return Promise.reject(new Error('An internal error has occurred.'))
    }

    return Promise.reject(err)
  }
)

// Ensuring this call completes before we allow Vue to do anything
const mountApp = () => {
  const app = createApp(App)

  app.use(router)
  app.use(Notifications)
  app.use(VueAxios, axios)
  app.use(createBootstrap())
  app.use(BootstrapVueNextCompat)
  app.use(BvModalCompat)
  app.use(VueCardFormat)
  app.use(aim)
  app.component('default-layout', Default)
  app.config.globalProperties.$bus = bus

  app.mount('#app')
}

// Do not block the whole frontend if the user is not logged in.
UserService.getUserInfo()
  .catch((err) => {
    if (err?.response?.status === 401 || err?.status === 401) {
      console.warn('User is not authenticated yet; continuing as guest.')
      return null
    }

    console.error('Failed to load network/user info:', err)
    return null
  })
  .finally(() => {
    mountApp()
  })

/* eslint-disable-next-line */
String.prototype.capitalize = function () {
  return this.charAt(0).toUpperCase() + this.slice(1)
}
